import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Palette, Layout, Type, Shield, ShieldAlert } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { BrandingConfig, AppSettings } from '../../types';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { useAuth } from '../../context/AuthContext';

interface BrandingFormProps {
  settings?: AppSettings;
}

export const BrandingForm: React.FC<BrandingFormProps> = ({ settings }) => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<BrandingConfig>({
    name: '', tagline: '', logo_url: '', banner_url: '', primary_color: '#FFDB00'
  });

  const isManager = profile?.role === 'system_admin' || profile?.role === 'org_manager';

  useEffect(() => {
    if (settings?.branding) setForm(settings.branding);
  }, [settings]);

  const updateMutation = useMutation({
    mutationFn: async (newBranding: BrandingConfig) => {
      if (!isManager) throw new Error("Unauthorized role.");
      
      const { data, error } = await supabase
        .from('app_settings')
        .update({ branding: newBranding })
        .eq('id', settings?.id || 1)
        .select();
        
      if (error) throw error;
      if (!data || data.length === 0) throw new Error("Settings not updated. The database rejected the modification. Ensure the Database RLS policy allows 'org_manager' to UPDATE 'app_settings'.");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminSettings'] });
      document.title = form.name || 'HFLI Data';
    },
    onError: (err: any) => alert(`Branding Update Blocked: ${err.message}`)
  });

  return (
    <div className="space-y-10 p-12 bg-white border border-slate-200 rounded-[2rem] shadow-xl">
      <div className="flex items-center gap-6 mb-4">
         <div className="w-16 h-16 bg-slate-900 text-[#FFDB00] rounded-2xl flex items-center justify-center shadow-2xl">
            <Palette size={32} />
         </div>
         <div>
            <h3 className="text-3xl font-black uppercase text-slate-900 tracking-tight">Identity & Design</h3>
            <p className="text-[10px] text-slate-400 font-black uppercase tracking-[0.3em] mt-1">Interface Personalization</p>
         </div>
      </div>

      {!isManager && (
        <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl flex items-center gap-3 text-amber-600 mb-6">
           <ShieldAlert size={18} />
           <p className="text-[10px] font-black uppercase tracking-widest leading-none">Settings restricted to Institutional Administrators.</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="space-y-8">
          <div className="space-y-6">
            <div className="flex items-center gap-2 text-[10px] font-black text-slate-900 uppercase tracking-widest mb-2">
               <Type size={14} className="text-blue-600" /> Nomenclature
            </div>
            <Input label="Application Identity" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="HFLI Data" disabled={!isManager} />
            <Input label="Strategic Tagline" value={form.tagline} onChange={e => setForm({...form, tagline: e.target.value})} disabled={!isManager} />
          </div>

          <div className="space-y-6 pt-6 border-t border-slate-50">
            <div className="flex items-center gap-2 text-[10px] font-black text-slate-900 uppercase tracking-widest mb-2">
               <Palette size={14} className="text-[#FFDB00]" /> Visual Language
            </div>
            <div className="space-y-2">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Accent Brand Color</label>
              <div className="flex gap-4">
                <input type="color" value={form.primary_color} className="w-20 h-14 p-1 bg-white border border-slate-200 rounded-xl cursor-pointer" onChange={e => setForm({...form, primary_color: e.target.value})} disabled={!isManager} />
                <Input value={form.primary_color} className="uppercase font-mono flex-1 py-4 text-center" onChange={e => setForm({...form, primary_color: e.target.value})} disabled={!isManager} />
              </div>
            </div>
          </div>
        </div>
        
        <div className="space-y-8">
          <div className="space-y-6">
            <div className="flex items-center gap-2 text-[10px] font-black text-slate-900 uppercase tracking-widest mb-2">
               <Layout size={14} className="text-purple-600" /> Digital Assets
            </div>
            <Input label="Logo Asset URL (SVG/PNG)" value={form.logo_url} onChange={e => setForm({...form, logo_url: e.target.value})} disabled={!isManager} />
            <Input label="Login Portal Banner URL" value={form.banner_url} onChange={e => setForm({...form, banner_url: e.target.value})} disabled={!isManager} />
            
            <div className="grid grid-cols-2 gap-6 pt-4">
               <div className="p-6 bg-slate-50 rounded-2xl border border-dashed border-slate-200 flex flex-col items-center justify-center min-h-[160px] relative overflow-hidden group shadow-inner">
                  {form.logo_url ? <img src={form.logo_url} className="max-h-16 object-contain z-10" /> : <Shield size={32} className="text-slate-200" />}
                  <span className="text-[8px] font-black text-slate-300 uppercase tracking-[0.3em] mt-4 z-10">Brand Mark</span>
               </div>
               <div className="p-6 bg-slate-50 rounded-2xl border border-dashed border-slate-200 flex flex-col items-center justify-center min-h-[160px] overflow-hidden relative group shadow-inner">
                  {form.banner_url ? (
                    <>
                      <img src={form.banner_url} className="absolute inset-0 w-full h-full object-cover opacity-20" />
                      <div className="absolute inset-0 bg-gradient-to-t from-white/80 to-transparent" />
                    </>
                  ) : <div className="w-full h-2 bg-slate-200 rounded" />}
                  <span className="relative text-[8px] font-black text-slate-400 uppercase tracking-[0.3em] mt-auto">Login Banner</span>
               </div>
            </div>
          </div>
        </div>
      </div>

      {isManager && (
        <div className="flex justify-end pt-10 border-t border-slate-100">
          <Button onClick={() => updateMutation.mutate(form)} className="px-12 py-5 shadow-2xl" isLoading={updateMutation.isPending} icon={<Save size={20} />}>
            Commit Brand Updates
          </Button>
        </div>
      )}
    </div>
  );
};